/** @jsxImportSource davaux/client */
import { createSignal } from 'davaux/client'

/** A selectable option in the Select dropdown. */
export interface SelectOption {
  /** Submitted form value. */
  value: string
  /** Display label shown in the list and target. */
  label: string
  /** When true, the option is shown but cannot be selected. */
  disabled?: boolean
}

export default function Select({
  data = [],
  value: initialValue = '',
  placeholder = 'Select an option',
  label,
  description,
  error,
  disabled = false,
  clearable = false,
  searchable = false,
  id,
  name,
  required = false,
  withAsterisk = false,
  nothingFound = 'Nothing found',
}: {
  data?: (string | SelectOption)[]
  value?: string
  placeholder?: string
  label?: string
  description?: string
  error?: string
  disabled?: boolean
  clearable?: boolean
  searchable?: boolean
  id?: string
  name?: string
  required?: boolean
  withAsterisk?: boolean
  nothingFound?: string
}) {
  const [open, setOpen] = createSignal(false)
  const [value, setValue] = createSignal(initialValue)
  const [search, setSearch] = createSignal('')

  const options: SelectOption[] = data.map((d) =>
    typeof d === 'string' ? { value: d, label: d } : d,
  )

  const selected = () => options.find((o) => o.value === value())

  const filtered = () =>
    searchable && search()
      ? options.filter((o) => o.label.toLowerCase().includes(search().toLowerCase()))
      : options

  const hasAsterisk = required || withAsterisk
  const hasError = !!error

  function selectOption(opt: SelectOption) {
    if (opt.disabled) return
    setValue(opt.value)
    setSearch('')
    setOpen(false)
  }

  function clearValue(e: Event) {
    e.stopPropagation()
    setValue('')
    setSearch('')
  }

  function handleTargetClick() {
    if (!disabled) setOpen(!open())
  }

  function handleRootKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') setOpen(false)
  }

  return (
    <div
      class={`dv-input-wrapper dv-select`}
      data-disabled={disabled || undefined}
      data-error={hasError || undefined}
      onKeyDown={handleRootKeyDown}
    >
      {label && (
        <label class='dv-input-label' for={id} data-required={hasAsterisk || undefined}>
          {label}
          {hasAsterisk && (
            <span class='dv-input-asterisk' aria-hidden='true'>
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {description && <p class='dv-input-description'>{description}</p>}
      <div
        class='dv-select-target'
        id={id}
        role='combobox'
        aria-expanded={() => open()}
        aria-haspopup='listbox'
        tabIndex={disabled ? -1 : 0}
        data-opened={() => open() || undefined}
        onClick={handleTargetClick}
      >
        {() =>
          searchable && open() ? (
            <input
              class='dv-select-search'
              value={search()}
              placeholder={selected() ? selected()!.label : placeholder}
              onInput={(e: Event) => setSearch((e.target as HTMLInputElement).value)}
              onClick={(e: Event) => e.stopPropagation()}
              ref={(el: HTMLInputElement) => requestAnimationFrame(() => el.focus())}
            />
          ) : (
            <span class='dv-select-value'>
              {() =>
                selected() ? (
                  selected()!.label
                ) : (
                  <span class='dv-select-placeholder'>{placeholder}</span>
                )
              }
            </span>
          )
        }
        {() =>
          clearable && value() ? (
            <button
              type='button'
              class='dv-select-clear'
              onClick={clearValue}
              aria-label='Clear'
              tabIndex={-1}
            >
              ×
            </button>
          ) : undefined
        }
        <span class='dv-select-chevron' aria-hidden='true'>
          ▾
        </span>
      </div>
      <div class='dv-select-dropdown' role='listbox' data-opened={() => open() || undefined}>
        {() =>
          filtered().map((opt) => (
            <div
              class='dv-select-option'
              role='option'
              aria-selected={opt.value === value()}
              data-selected={opt.value === value() || undefined}
              data-disabled={opt.disabled || undefined}
              onClick={() => selectOption(opt)}
            >
              {opt.label}
            </div>
          ))
        }
        {() => filtered().length === 0 && <div class='dv-select-empty'>{nothingFound}</div>}
      </div>
      {name && <input type='hidden' name={name} value={() => value()} />}
      {hasError && <p class='dv-input-error'>{error}</p>}
    </div>
  )
}
